import { z } from 'zod';
import { sessionHandle } from './bpSession.js';
import { getSession } from './session.js';

/**
 * The HTTP transport, shared by every app.
 *
 * **Only the transport is here — no endpoint schemas and no endpoint
 * functions.** Those are per app: `apps/web` knows about folders and the roster
 * and `apps/admin` knows about the fleet, and neither should be able to see the
 * other's contract. A package that knew what a `RecycleBin` was would be a
 * package both apps had to agree about.
 *
 * Every response is parsed with a Zod schema at the boundary, so a backend
 * shape change surfaces here with a clear message instead of as `undefined`
 * deep inside a component.
 *
 * Do not mock this module in tests — mock the network with MSW. Tests then
 * exercise the real parsing and error handling, which is where the bugs are.
 */

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

/**
 * The transport, bound to one API origin.
 *
 * **A factory rather than a module-level setting**, and that is the one shape
 * decision in this file. The base URL used to be read from `env` at call time,
 * which a package cannot do — it has no env of its own. The alternatives were a
 * `configureClient()` called once at startup, or this. A setter would add an
 * initialisation order that nothing enforces and whose omission surfaces as a
 * request to `undefined/api/me`; a factory cannot be forgotten, because there
 * is no `request` to call until you have made one.
 */
export interface ApiTransport {
  // **Function-typed properties, not method shorthand.** These are destructured
  // at every call site (`const { request, postJson } = createTransport(…)`), and
  // method shorthand declares an implicit `this` — which `@typescript-eslint`'s
  // `unbound-method` correctly flags the moment one is separated from its
  // object. They are closures over `baseUrl` and bind nothing, so the arrow form
  // is both the honest declaration and the one that lets them travel.
  readonly request: <T>(path: string, schema: z.ZodType<T>, init?: RequestInit) => Promise<T>;
  readonly postJson: <T>(path: string, schema: z.ZodType<T>, body?: unknown) => Promise<T>;
  readonly putJson: <T>(path: string, schema: z.ZodType<T>, body?: unknown) => Promise<T>;
  readonly patchJson: <T>(path: string, schema: z.ZodType<T>, body?: unknown) => Promise<T>;
  readonly delJson: <T>(path: string, schema: z.ZodType<T>, body?: unknown) => Promise<T>;
}

/**
 * The `Authorization` header for this request, or `''` when there is no
 * session.
 *
 * Under `VITE_USE_MOCKS=true` nothing ever registers one (ADR-0027), so this
 * answers `''` and `request()` sends byte-identical headers to the ones it sent
 * before sign-in existed. That is what let the whole existing suite stand.
 *
 * **A registered session is not the same thing as a bearer to send.** A session
 * the browser holds as a cookie carries no `getAccessToken` (ADR-0045), and the
 * two cases collapse to the same answer here: no header, and `fetch`'s default
 * `credentials: 'same-origin'` sends the cookie without being asked.
 *
 * A failure here is a **401**, not a network error: the token could not be
 * acquired, which is a credential problem, and reporting it as "could not reach
 * the API" would put an offline message on a screen whose retry cannot help.
 */
async function bearer(): Promise<string> {
  const session = getSession();
  if (session?.getAccessToken === undefined) return '';

  try {
    return `Bearer ${await session.getAccessToken()}`;
  } catch (cause) {
    throw new ApiError(401, 'Could not acquire an access token.', cause);
  }
}

function safeJsonParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export function createTransport(baseUrl: string): ApiTransport {
  const url = (path: string): string => {
    const base = baseUrl.replace(/\/$/, '');
    return `${base}${path.startsWith('/') ? path : `/${path}`}`;
  };

  async function request<T>(path: string, schema: z.ZodType<T>, init?: RequestInit): Promise<T> {
    // **Above the try, deliberately.** Inside it, a token failure would be
    // caught by the network branch below and reported as an offline error.
    const authorization = await bearer();
    // Read per request rather than captured at `createTransport`: it is written
    // by a sign-in that happens after the transport is built, and cleared by a
    // sign-out that happens while it is still in use.
    const handle = sessionHandle();

    let response: Response;
    try {
      response = await fetch(url(path), {
        ...init,
        headers: {
          Accept: 'application/json',
          ...init?.headers,
          // **Last on purpose, and not a formatting accident.** `init.headers`
          // merges after the defaults so a route can set its own content type;
          // these sit below it so no route can set its own identity.
          ...(authorization === '' ? {} : { Authorization: authorization }),
          // **The session, where the browser has to carry it** (ADR-0059).
          //
          // This module's header used to say it "does not change by one line"
          // to accommodate the session, because `fetch` defaults to
          // `credentials: 'same-origin'` and the cookie rode along unasked. That
          // is still true of every one-origin build, which is prod: `handle` is
          // `undefined` there, this spreads to nothing, and the request is
          // byte-identical to what it has always been.
          //
          // A two-origin build has no cookie to ride along, so it sends the same
          // credential here instead. Not `Authorization: Bearer` — ADR-0058
          // deleted every reader of a bearer token from the agency routes and
          // `index.test.ts` asserts none has come back, so a session wearing
          // that scheme would look exactly like the credential that decision
          // removed.
          ...(handle === undefined ? {} : { 'x-bpf-session': handle }),
        },
      });
    } catch (cause) {
      throw new ApiError(0, `Could not reach the API at ${url(path)}.`, cause);
    }

    const text = await response.text();
    const body = text === '' ? null : safeJsonParse(text);

    if (!response.ok) {
      throw new ApiError(response.status, `Request to ${path} failed.`, body);
    }

    const parsed = schema.safeParse(body);
    if (!parsed.success) {
      throw new ApiError(
        response.status,
        `Unexpected response shape from ${path}: ${z.prettifyError(parsed.error)}`,
        body,
      );
    }

    return parsed.data;
  }

  function sendJson<T>(
    method: string,
    path: string,
    schema: z.ZodType<T>,
    body?: unknown,
  ): Promise<T> {
    return request(path, schema, {
      method,
      // An absent body sends no `Content-Type` at all, rather than one
      // describing nothing. `PATCH /upload-sessions/{sid}` is called that way on
      // purpose: "just renew my credential" is the ordinary case, and the API
      // treats an empty body as exactly that.
      ...(body === undefined
        ? {}
        : { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }),
    });
  }

  return {
    request,
    /** POST with a JSON body. The one place the header and the encoding live. */
    postJson: (path, schema, body) => sendJson('POST', path, schema, body),
    putJson: (path, schema, body) => sendJson('PUT', path, schema, body),
    patchJson: (path, schema, body) => sendJson('PATCH', path, schema, body),
    /**
     * DELETE, expecting a JSON body back — and optionally carrying one.
     *
     * The API answers a delete with the row's new shape rather than a `204`,
     * and that is deliberate on both sides: `request` parses every response
     * against a schema, and a `204` arrives here as `null`, which an object
     * schema rejects as an unexpected shape.
     *
     * **The optional request body is not decoration.** A destructive route may
     * demand an attestation in the body — `DELETE /operator/agencies/{id}`
     * requires `{ understood: true }` (ADR-0096) — and without a way to send one
     * the call arrives with no body at all, which the API rejects as *"The
     * request body must be JSON."* before the schema ever runs. `undefined`
     * still sends no `Content-Type`, so every bodiless delete is unchanged.
     */
    delJson: (path, schema, body) => sendJson('DELETE', path, schema, body),
  };
}
